import React, { RefObject, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'

import { Box, Typography } from '@mui/material'
import { Div, IntersectionObserver, ListingSkeletonLoader } from '@vividseats/vivid-ui-kit'
import { IntersectionObserverProps } from '@vividseats/vivid-ui-kit/dist/components/intersection-observer'
import debounce from 'lodash/debounce'
import { useRouter } from 'next/router'

import InfiniteScroll from '@/components/shared/infinite-scroll'
import { STYLES } from '@/constants'
import ListingsContext from '@/context/listings'
import mapContext from '@/context/map/context'
import productionDetailsContext from '@/context/production-details'
import useWindowSize from '@/hooks/use-window-size'
import type { ComposedListing, ListingsGroup } from '@/types/app-types'
import { trackViewProduction } from '@/utils/analytics'
import { trackEvent } from '@/utils/analytics/ga360'

import filterContext from '../context'
import OverFiltered from '../filters/over-filtered'
import ListingComponent, { ListingComponentProps } from '../listing-component/component'
import NoTicketsAvailable from '../no-tickets-available'
import TicketsResponseError from '../tickets-response-error'

export interface Props {
    className?: string
    exposedFilter?: boolean
    showUsd?: boolean
    isMapOpen?: boolean
    mapContainerRef?: RefObject<HTMLDivElement>
    scrollRef?: RefObject<HTMLDivElement>
}

const intersectionObserverProps: TypedOmit<IntersectionObserverProps, 'onIntersection'> = {
    intersectionOptions: {
        rootMargin: '5px',
    },
}

// Pagination control constants
const PAGINATION_STEP = 8
const DEFAULT_INITIAL_PAGE_SIZE = 20
const LISTING_ITEM_HEIGHT = 68

const ListingsList: React.FC<Props> = ({
    className,
    exposedFilter = false,
    showUsd = false,
    isMapOpen,
    mapContainerRef,
    scrollRef,
}) => {
    const {
        selectors: filterSelectors,
        state: { quantityFilterState },
    } = useContext(filterContext)
    const { state: listingsState, selectors: listingsSelectors } = useContext(ListingsContext)
    const { state: productionDetailsState } = useContext(productionDetailsContext)
    const {
        selectors: {
            venueMapState: { setSectionPriceTooltip },
            isMapboxMapEnabled,
        },
    } = useContext(mapContext)
    const { hasListings } = listingsSelectors
    const [breakingPoint, setBreakingPoint] = useState(0)
    const [heightOffset, setHeightOffset] = useState(0)
    const listingRowRef = useRef<HTMLDivElement>(null)
    const filteredListings = filterSelectors.filteredListingsSelector
    const numFilteredListings = filteredListings.length
    const isInitialListingsState = !listingsState.data && !listingsState.error
    const showLoader = listingsState.fetching
    const isLoading = isInitialListingsState || (listingsState.fetching ?? true)
    const isOverFiltered = hasListings && numFilteredListings === 0
    const windowSize = useWindowSize()
    const isDesktop = windowSize.width && windowSize.width > STYLES.breakpoint.desktop
    const { selectedSection: selectedSectionStyles } = STYLES
    const selectionSectionStyleString = `stroke: ${selectedSectionStyles.stroke}; stroke-width: ${selectedSectionStyles['stroke-width']}; fill: ${selectedSectionStyles.fill};`
    const [listings, setListings] = useState<(ComposedListing | ListingsGroup)[]>([])

    const {
        pathname,
        query: { slug },
    } = useRouter()

    const unhighlightAllSections = useCallback(() => {
        const mapEl = typeof document !== 'undefined' ? document.querySelector('[class*=original-map]') : null
        const allSections = mapEl?.querySelectorAll('[data-svg-section-id]')

        if (allSections && allSections.length) {
            allSections.forEach((section) => {
                const currentStyleString = section.getAttribute('style') || ''

                section.setAttribute('style', currentStyleString.replace(selectionSectionStyleString, ''))
            })
        }
        if (isMapboxMapEnabled) setSectionPriceTooltip({ sectionId: '' }) // @vividseats/venue-maps-component
    }, [selectionSectionStyleString, setSectionPriceTooltip, isMapboxMapEnabled])

    const highlightSection = useCallback(
        (sectionId: string) => {
            const mapEl = typeof document !== 'undefined' ? document.querySelector('[class*=original-map]') : null
            const highlightedSection = mapEl?.querySelector(`[data-svg-section-id="${sectionId}"]`)

            unhighlightAllSections()

            if (highlightedSection) {
                highlightedSection.setAttribute(
                    'style',
                    selectionSectionStyleString + highlightedSection.getAttribute('style') || '',
                )
            }
            if (isMapboxMapEnabled) setSectionPriceTooltip({ sectionId }) // @vividseats/venue-maps-component
        },
        [selectionSectionStyleString, unhighlightAllSections, setSectionPriceTooltip, isMapboxMapEnabled],
    )

    const onScroll = debounce((listings, topListing: HTMLDivElement, margin: number, height: number) => {
        if (numFilteredListings === 0) return

        topListing.style.backgroundColor = ''
        listings.forEach((listing: HTMLDivElement) => {
            // TODO: WFO-659 -- if optimizely experiment with exposedFilter wins, figure out more graceful way to handle offset
            const topOffset = listing?.getBoundingClientRect().top + height / 0.6 - heightOffset
            const sectionId = listing?.getAttribute('data-sectionid') || ''
            const shouldHighlight = topOffset >= breakingPoint && topOffset <= breakingPoint + height + margin
            const parent = listing.closest('#row-container') as HTMLDivElement

            if (shouldHighlight) {
                parent.style.backgroundColor = STYLES.highlightedListingLightGray.backgroundColor
                highlightSection(sectionId)
            } else {
                parent.style.backgroundColor = ''
            }
        })
    }, 10)

    useEffect(() => {
        if (!exposedFilter) {
            return
        }

        const exposedFilterHeight = document.querySelector('[id=exposed-filter]')?.clientHeight || 0

        setHeightOffset(exposedFilterHeight)
    }, [exposedFilter])

    useEffect(() => {
        const production = productionDetailsState.data
        if (!!production && !listingsState.fetching) {
            trackViewProduction({ production })
        }
    }, [productionDetailsState.data, listingsState.fetching])

    useEffect(() => {
        if (isMapOpen) {
            setBreakingPoint(mapContainerRef?.current?.getBoundingClientRect().bottom || 0)
        }
    }, [isMapOpen])

    useEffect(() => {
        const currentScrollRef = scrollRef?.current
        const listingElements: NodeListOf<HTMLDivElement> | undefined =
            currentScrollRef?.querySelectorAll('[data-sectionid]')
        const topListing = listingElements && (listingElements[0] as HTMLDivElement)
        const height = topListing ? topListing.getBoundingClientRect().height : 0
        const margin = topListing ? parseInt(window.getComputedStyle(topListing).getPropertyValue('margin-top')) * 2 : 0
        const scrollHandler = () => {
            listingElements && topListing && onScroll(listingElements, topListing, margin, height)
        }
        const topListingSectionId = topListing?.getAttribute('data-sectionid') || ''

        if (!isDesktop && isMapOpen && !isLoading && numFilteredListings > 0) {
            highlightSection(topListingSectionId)

            if (topListing) {
                const parent = topListing.closest('#row-container') as HTMLDivElement
                parent.style.backgroundColor = STYLES.highlightedListingLightGray.backgroundColor
            }

            currentScrollRef?.addEventListener('scroll', scrollHandler)
        }

        return () => {
            listingElements && listingElements.forEach((listing) => (listing.style.backgroundColor = ''))
            currentScrollRef?.removeEventListener('scroll', scrollHandler)
        }
    }, [isDesktop, isMapOpen, isLoading, numFilteredListings, breakingPoint, listings, highlightSection])

    useEffect(() => {
        if (!productionDetailsState.fetching && !showLoader && window && window.history.length > 1) {
            trackEvent('TicketPageLoad', 'load', undefined, undefined, true, {
                medianPrice: listingsState.data?.global?.[0]?.mp,
                listingCount: productionDetailsState.data?.listingCount,
                ticketsAvailableCount: productionDetailsState.data?.ticketCount,
            })
        }
    }, [productionDetailsState, showLoader, listingsState.data?.global])

    const production = productionDetailsState.data

    useEffect(() => {
        if (scrollRef?.current?.scrollTop) scrollRef.current.scrollTop = 0
    }, [scrollRef, numFilteredListings, filterSelectors.getCurrentSortByButtonTextSelector])

    // Calculates the base number of listings to be rendered based on the viewport height and
    // sets the base listings to be rendered on the infinite scroll list
    // On page load, the first X items from the listings list (i.e., filteredListings) will be rendered;
    // if filters are applied, filteredListings will be updated and the the first X items from the updated
    // list will be rendered, resetting any previous scrolls
    useEffect(() => {
        const listingsContainerHeight = windowSize.height * 0.85 // listings container takes ~85% of viewport height
        const listingsListPaginationSize =
            Math.ceil(listingsContainerHeight / LISTING_ITEM_HEIGHT) || DEFAULT_INITIAL_PAGE_SIZE
        setListings(filteredListings.slice(0, listingsListPaginationSize))
    }, [filteredListings])

    const itemToItemComponentProps = useCallback(
        (item: ComposedListing | ListingsGroup) => {
            const obj: ListingComponentProps = {
                id: item.id,
                listing: item,
                pathname: pathname || '',
                production: production,
                showUsd: showUsd,
                slug: slug,
                quantityFilterState: quantityFilterState,
                highlightSection: highlightSection,
                isDesktop: isDesktop,
            }

            return obj
        },
        [isDesktop, pathname, production, quantityFilterState, showUsd, slug, highlightSection],
    )

    const loadMoreHandler = useCallback(() => {
        setListings((prevListings) => filteredListings.slice(0, prevListings.length + PAGINATION_STEP))
    }, [filteredListings])

    const [endOfListVisible, setEndOfListVisible] = useState<boolean | undefined>(undefined)

    // Because handleIntersection will always be called with isVisible being false initially,
    // we cannot trust the first result. So this debounced function will be called & if there is a follow up
    // call to handleIntersection, it will cancel the first (erroneous) call
    const debouncedSetEndOfListVisible = useMemo(() => debounce(setEndOfListVisible, 250), [])

    const handleIntersection = useCallback(
        (isVisible: boolean) => {
            debouncedSetEndOfListVisible.cancel()
            debouncedSetEndOfListVisible(isVisible)
        },
        [debouncedSetEndOfListVisible],
    )

    useEffect(() => {
        if (endOfListVisible === undefined) return
        setEndOfListVisible(undefined)
    }, [numFilteredListings])

    return (
        <div className={className} ref={listingRowRef} onMouseLeave={() => isDesktop && unhighlightAllSections()}>
            <ListingSkeletonLoader isLoading={isLoading} rowCount={25}>
                {!hasListings && !listingsState.error && <NoTicketsAvailable />}
                {listingsState.error && <TicketsResponseError />}
                {isOverFiltered && <OverFiltered />}
                <InfiniteScroll
                    key={'InfiniteScroll'}
                    isFetching={false}
                    items={listings}
                    totalItems={numFilteredListings}
                    loadMoreHandler={loadMoreHandler}
                    ItemComponent={ListingComponent}
                    itemToItemComponentProps={itemToItemComponentProps}
                    intersectionObserverProps={intersectionObserverProps}
                />

                {endOfListVisible === undefined && <IntersectionObserver onIntersection={handleIntersection} />}

                {hasListings && listings.length === numFilteredListings && endOfListVisible === false && (
                    <Div desktopDisplay="none">
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            paddingTop={4}
                            paddingBottom={4}
                            gap="4px"
                            data-testid="list-end-message"
                        >
                            <Typography variant="small-bold">You&apos;ve reached the end of the list</Typography>
                            <Typography variant="small-regular">Scroll up to continue shopping</Typography>
                        </Box>
                    </Div>
                )}
            </ListingSkeletonLoader>
        </div>
    )
}

export default ListingsList
